import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FeatureMatrix, PlatformFeature } from '@up2d8/contracts';
import { LoadingState } from '@up2d8/ui';
import { useSession } from '../../shared/session';
import {
  AdminIcon,
  AdminPage,
  Badge,
  Btn,
  Card,
  CardHead,
  CardPad,
  Empty,
  Modal,
  Note,
  PageHead,
  SearchField,
  Table,
  TabPanel,
  Tabs,
  Toggle,
  Toolbar,
  useAdminToast,
  type TabItem,
} from '../admin-kit';
import { FilterMenu } from './filter-menu';

type TabKey = 'defaults' | 'matrix';

/**
 * Functies over alle organisaties heen (MST-4).
 *
 * "Standaard": per functie de waarde uit de code, en de platformstandaard die Master erboven zet. Die
 * geldt voor elke organisatie zonder eigen instelling, ook de bestaande. "Matrix": per organisatie
 * wat ze effectief heeft, en per functie één knop om ze voor iedereen aan of uit te zetten.
 */
export function PlatformFeaturesPage() {
  const { t } = useTranslation();
  const toast = useAdminToast();
  const [tab, setTab] = useState<TabKey>('defaults');

  const tabs: TabItem<TabKey>[] = [
    { key: 'defaults', label: t('master.featuresPage.tabDefaults') },
    { key: 'matrix', label: t('master.featuresPage.tabMatrix') },
  ];

  return (
    <AdminPage>
      <PageHead title={t('master.featuresPage.title')} sub={t('master.featuresPage.sub')} />
      <Tabs
        label={t('master.featuresPage.title')}
        idPrefix="features"
        tabs={tabs}
        value={tab}
        onChange={setTab}
      />
      <TabPanel idPrefix="features" tabKey={tab} className="a-stack">
        {tab === 'defaults' ? (
          <DefaultsTab onToast={toast.show} />
        ) : (
          <MatrixTab onToast={toast.show} />
        )}
      </TabPanel>
      {toast.element}
    </AdminPage>
  );
}

function useFeatures() {
  const { api } = useSession();
  return useQuery({
    queryKey: ['platform', 'platform-features'],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/platform/features');
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as { items: PlatformFeature[] }).items;
    },
  });
}

// --- Standaard ---------------------------------------------------------------------------------

function DefaultsTab({ onToast }: { onToast: (message: string) => void }) {
  const { t } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();
  const features = useFeatures();
  const [zoek, setZoek] = useState('');
  const [module, setModule] = useState('all');

  const update = useMutation({
    mutationFn: async (defaults: Record<string, boolean | null>) => {
      const { error } = await api.PUT('/api/v1/platform/features/defaults', {
        body: { defaults } as never,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      onToast(t('master.featuresPage.saved'));
      await queryClient.invalidateQueries({ queryKey: ['platform'] });
    },
  });

  const modules = [...new Set((features.data ?? []).map((feature) => feature.module))].sort();
  const term = zoek.trim().toLowerCase();
  const zichtbaar = (features.data ?? []).filter(
    (feature) =>
      (module === 'all' || feature.module === module) &&
      (!term ||
        feature.key.toLowerCase().includes(term) ||
        feature.description.toLowerCase().includes(term)),
  );

  return (
    <Card>
      <Toolbar>
        <SearchField label={t('beheer.platform.features.search')} value={zoek} onChange={setZoek} />
        <FilterMenu
          label={t('beheer.platform.features.module')}
          value={module}
          onChange={setModule}
          options={[
            { value: 'all', label: t('beheer.platform.filters.all') },
            ...modules.map((value) => ({ value, label: value })),
          ]}
        />
      </Toolbar>
      <CardPad>
        <p className="a-muted">{t('master.featuresPage.defaultsIntro')}</p>
        {update.isError ? (
          <Note tone="danger" role="alert">
            {t('errors.generic')}
          </Note>
        ) : null}
        {features.isPending ? (
          <LoadingState rows={4} label={t('common.loading')} />
        ) : features.isError ? (
          <Note tone="danger" role="alert">
            {t('errors.generic')}
          </Note>
        ) : zichtbaar.length === 0 ? (
          <Empty
            icon={<AdminIcon name="search" size={24} />}
            title={t('beheer.platform.features.none')}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {zichtbaar.map((feature) => (
              <DefaultRow
                key={feature.key}
                feature={feature}
                busy={update.isPending}
                onChange={(enabled) => update.mutate({ [feature.key]: enabled })}
              />
            ))}
          </div>
        )}
      </CardPad>
    </Card>
  );
}

function DefaultRow({
  feature,
  busy,
  onChange,
}: {
  feature: PlatformFeature;
  busy: boolean;
  onChange: (enabled: boolean | null) => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const gezet = feature.platformDefault !== null;

  return (
    <div className="a-switch" data-testid={`platform-feature-${feature.key}`}>
      <div className="a-switch-text">
        <div className="a-item-top">
          <span className="a-switch-title" id={`${id}-title`}>
            {feature.description}
          </span>
          <Badge tone={gezet ? 'info' : 'neutral'}>
            {gezet ? t('master.featuresPage.byMaster') : t('master.featuresPage.byCode')}
          </Badge>
          <span className="a-meta">{feature.module}</span>
        </div>
        <div className="a-mono a-meta" id={`${id}-key`}>
          {feature.key}
        </div>
        <div className="a-meta">
          {t('master.featuresPage.usage', {
            on: feature.tenantsOn,
            total: feature.tenantsTotal,
            own: feature.tenantsOverridden,
          })}
          {gezet
            ? ` · ${t('master.featuresPage.codeSays', {
                value: feature.codeDefault
                  ? t('master.featuresPage.on')
                  : t('master.featuresPage.off'),
              })}`
            : ''}
        </div>
      </div>
      {gezet ? (
        <Btn variant="ghost" size="sm" disabled={busy} onClick={() => onChange(null)}>
          {t('master.featuresPage.backToCode')}
        </Btn>
      ) : null}
      <Toggle
        checked={feature.defaultValue}
        onChange={(enabled) => onChange(enabled)}
        labelledBy={`${id}-title`}
        describedBy={`${id}-key`}
        disabled={busy}
      />
    </div>
  );
}

// --- Matrix ------------------------------------------------------------------------------------

interface BulkPlan {
  flagKey: string;
  enabled: boolean | null;
}

function MatrixTab({ onToast }: { onToast: (message: string) => void }) {
  const { t } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();
  const features = useFeatures();
  const [module, setModule] = useState('all');
  const [zoek, setZoek] = useState('');
  const [plan, setPlan] = useState<BulkPlan | null>(null);

  const matrix = useQuery({
    queryKey: ['platform', 'feature-matrix'],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/platform/features/matrix');
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as FeatureMatrix;
    },
  });

  const bulk = useMutation({
    mutationFn: async (input: BulkPlan) => {
      const { data, error } = await api.POST('/api/v1/platform/features/bulk', {
        body: { ...input, tenantIds: [] } as never,
      });
      if (error || !data) throw error ?? new Error('Mislukt');
      return data as { changed: number };
    },
    onSuccess: async (result) => {
      setPlan(null);
      onToast(t('master.featuresPage.bulkDone', { count: result.changed }));
      await queryClient.invalidateQueries({ queryKey: ['platform'] });
    },
  });

  // Bij openen de fout van een vorige poging wissen; anders staat die er meteen weer.
  const openBulk = (next: BulkPlan) => {
    bulk.reset();
    setPlan(next);
  };

  const perKey = new Map((features.data ?? []).map((feature) => [feature.key, feature]));
  const modules = [...new Set((features.data ?? []).map((feature) => feature.module))].sort();
  const kolommen = (matrix.data?.flags ?? []).filter(
    (key) => module === 'all' || perKey.get(key)?.module === module,
  );
  const term = zoek.trim().toLowerCase();
  const rijen = (matrix.data?.rows ?? []).filter(
    (row) =>
      !term || row.name.toLowerCase().includes(term) || row.code.toLowerCase().includes(term),
  );

  return (
    <Card>
      <Toolbar>
        <SearchField label={t('tenants.search')} value={zoek} onChange={setZoek} />
        <FilterMenu
          label={t('beheer.platform.features.module')}
          value={module}
          onChange={setModule}
          options={[
            { value: 'all', label: t('beheer.platform.filters.all') },
            ...modules.map((value) => ({ value, label: value })),
          ]}
        />
      </Toolbar>
      <CardPad>
        <p className="a-muted">{t('master.featuresPage.matrixIntro')}</p>
      </CardPad>
      {matrix.isPending || features.isPending ? (
        <LoadingState rows={4} label={t('common.loading')} />
      ) : matrix.isError ? (
        <CardPad>
          <Note tone="danger" role="alert">
            {t('errors.generic')}
          </Note>
        </CardPad>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <Table label={t('master.featuresPage.tabMatrix')}>
            <thead>
              <tr>
                <th>{t('tenants.name')}</th>
                {kolommen.map((key) => (
                  <th key={key} title={perKey.get(key)?.description} className="a-mono">
                    <div>{key}</div>
                    <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                      <Btn
                        variant="ghost"
                        size="sm"
                        onClick={() => openBulk({ flagKey: key, enabled: true })}
                      >
                        {t('master.featuresPage.allOn')}
                      </Btn>
                      <Btn
                        variant="ghost"
                        size="sm"
                        onClick={() => openBulk({ flagKey: key, enabled: false })}
                      >
                        {t('master.featuresPage.allOff')}
                      </Btn>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rijen.map((row) => (
                <tr key={row.tenantId}>
                  <td>
                    <Link
                      to="/master/organisaties/$tenantId"
                      params={{ tenantId: row.tenantId }}
                      className="a-name a-link"
                    >
                      {row.name}
                    </Link>
                  </td>
                  {kolommen.map((key) => {
                    const aan = row.values[key];
                    const eigen = row.overridden.includes(key);
                    return (
                      <td key={key} data-testid={`matrix-${row.code}-${key}`}>
                        <Badge tone={aan ? 'ok' : 'neutral'}>
                          {aan ? t('master.featuresPage.on') : t('master.featuresPage.off')}
                        </Badge>
                        {eigen ? (
                          <span className="a-meta"> · {t('master.featuresPage.own')}</span>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <Modal
        open={plan !== null}
        onClose={() => setPlan(null)}
        title={t('master.featuresPage.bulkTitle')}
        sub={
          plan
            ? t(plan.enabled ? 'master.featuresPage.bulkOn' : 'master.featuresPage.bulkOff', {
                key: plan.flagKey,
                count: matrix.data?.rows.length ?? 0,
              })
            : undefined
        }
        footer={
          <>
            <Btn variant="sec" onClick={() => setPlan(null)}>
              {t('common.cancel')}
            </Btn>
            {plan ? (
              <Btn
                variant="sec"
                disabled={bulk.isPending}
                onClick={() => bulk.mutate({ flagKey: plan.flagKey, enabled: null })}
              >
                {t('master.featuresPage.bulkReset')}
              </Btn>
            ) : null}
            <Btn disabled={bulk.isPending || !plan} onClick={() => plan && bulk.mutate(plan)}>
              {t('master.featuresPage.bulkConfirm')}
            </Btn>
          </>
        }
      >
        <p className="a-muted">{t('master.featuresPage.bulkExplain')}</p>
        {bulk.isError ? (
          <Note tone="danger" role="alert">
            {t('errors.generic')}
          </Note>
        ) : null}
      </Modal>
    </Card>
  );
}

/** Voor het startscherm (MST-5): een kaart met de functies die het vaakst afwijken. */
export function FeaturesSummaryCard() {
  const { t } = useTranslation();
  const features = useFeatures();
  const afwijkend = (features.data ?? [])
    .filter((feature) => feature.tenantsOverridden > 0 || feature.platformDefault !== null)
    // De meest afwijkende eerst: de kaart belooft "het vaakst afwijken".
    .sort((a, b) => b.tenantsOverridden - a.tenantsOverridden)
    .slice(0, 5);
  return (
    <Card>
      <CardHead title={t('master.featuresPage.title')} />
      <CardPad>
        {features.isPending ? (
          <LoadingState rows={2} label={t('common.loading')} />
        ) : afwijkend.length === 0 ? (
          <p className="a-muted">{t('master.featuresPage.allDefault')}</p>
        ) : (
          <ul className="a-stack">
            {afwijkend.map((feature) => (
              <li key={feature.key}>
                <span className="a-mono">{feature.key}</span> ·{' '}
                {t('master.featuresPage.usage', {
                  on: feature.tenantsOn,
                  total: feature.tenantsTotal,
                  own: feature.tenantsOverridden,
                })}
              </li>
            ))}
          </ul>
        )}
      </CardPad>
    </Card>
  );
}
